/**
 * TaxonomyValueDisplay component - Display extracted taxonomy values
 * Shows AI-extracted or user-confirmed values with confidence indicators
 */

import { AlertCircle, CheckCircle, Info, Pencil, RefreshCw, User } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';
import type { TaxonomyExtraction, TaxonomyField, TaxonomySchema, TaxonomyValue } from '@/types/taxonomy';

import { TaxonomyValueEditor } from './TaxonomyValueEditor';

// ============================================================================
// TYPES
// ============================================================================

export interface TaxonomyValueDisplayProps {
  schema: TaxonomySchema;
  extraction: TaxonomyExtraction | null;
  showReasoning?: boolean;
  className?: string;
  onUpdateValue?: (fieldId: string, optionId: string) => Promise<void>;
  onReExtract?: (preserveUserConfirmed: boolean) => Promise<void>;
  isUpdating?: boolean;
  isReExtracting?: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get confidence color class based on confidence level
 * - 80-100%: High confidence (green)
 * - 50-79%: Medium confidence (yellow/warning)
 * - 0-49%: Low confidence (red)
 */
function getConfidenceColor(confidence: number): {
  bg: string;
  text: string;
  label: string;
} {
  if (confidence >= 80) {
    return {
      bg: 'bg-success/10',
      text: 'text-success',
      label: 'High',
    };
  }
  if (confidence >= 50) {
    return {
      bg: 'bg-warning/10',
      text: 'text-warning',
      label: 'Medium',
    };
  }
  return {
    bg: 'bg-danger/10',
    text: 'text-danger',
    label: 'Low',
  };
}

/**
 * Get the label for an option ID from the schema field
 */
function getOptionLabel(field: TaxonomyField, optionId: string): string {
  if (optionId === 'unknown') {
    return 'Unknown';
  }
  const option = field.options.find((opt) => opt.optionId === optionId);
  return option?.label || optionId;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface ValueCardProps {
  field: TaxonomyField;
  value: TaxonomyValue;
  showReasoning?: boolean;
  onEdit?: () => void;
  canEdit?: boolean;
}

function ValueCard({ field, value, showReasoning = false, onEdit, canEdit = false }: ValueCardProps) {
  const confidence = getConfidenceColor(value.confidence);
  const isUserConfirmed = value.source === 'user';
  const optionLabel = getOptionLabel(field, value.optionId);

  return (
    <div className="p-4 rounded-lg border border-border-subtle bg-surface space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-primary truncate">
            {field.name}
          </h4>
          {field.guidance && (
            <p className="text-xs text-muted mt-0.5 line-clamp-1" title={field.guidance}>
              {field.guidance}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Source indicator */}
          {isUserConfirmed && (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-pill bg-brand/10 text-brand text-xs">
              <User className="h-3 w-3" />
              <span>Confirmed</span>
            </div>
          )}

          {/* Edit button */}
          {canEdit && onEdit && (
            <button
              onClick={onEdit}
              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              aria-label={`Edit ${field.name}`}
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Value */}
      <div className="flex items-center gap-3">
        <span className="text-base font-medium text-primary">
          {optionLabel}
        </span>

        {/* Confidence indicator */}
        <div
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-pill text-xs',
            confidence.bg,
            confidence.text
          )}
        >
          {value.confidence >= 80 ? (
            <CheckCircle className="h-3 w-3" />
          ) : value.confidence >= 50 ? (
            <Info className="h-3 w-3" />
          ) : (
            <AlertCircle className="h-3 w-3" />
          )}
          <span>{value.confidence}%</span>
        </div>
      </div>

      {/* Reasoning */}
      {showReasoning && value.reasoning && (
        <div className="pt-2 border-t border-border-subtle">
          <p className="text-xs text-secondary leading-relaxed">
            {value.reasoning}
          </p>
        </div>
      )}

      {/* Override info */}
      {isUserConfirmed && value.overriddenAt && (
        <div className="text-xs text-muted">
          Overridden {new Date(value.overriddenAt).toLocaleDateString()}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function TaxonomyValueDisplay({
  schema,
  extraction,
  showReasoning = true,
  className,
  onUpdateValue,
  onReExtract,
  isUpdating = false,
  isReExtracting = false,
}: TaxonomyValueDisplayProps) {
  // State for editing
  const [editingField, setEditingField] = useState<TaxonomyField | null>(null);

  // Check if editing is available
  const canEdit = !!onUpdateValue;
  const canReExtract = !!onReExtract;

  // Handle save from editor
  const handleSaveValue = async (fieldId: string, optionId: string) => {
    if (onUpdateValue) {
      await onUpdateValue(fieldId, optionId);
      setEditingField(null);
    }
  };

  // Handle re-extract
  const handleReExtract = async () => {
    if (onReExtract) {
      await onReExtract(true); // Always preserve user-confirmed values
    }
  };

  // Handle no extraction
  if (!extraction) {
    return (
      <div className={cn('space-y-4', className)}>
        <div>
          <h3 className="text-sm font-semibold text-primary mb-1">
            Taxonomy Classification
          </h3>
          <p className="text-xs text-secondary">
            No taxonomy values extracted yet. Values will appear after analysis.
          </p>
        </div>
        <div className="py-8 text-center text-sm text-muted border-2 border-dashed border-border-subtle rounded-lg">
          Awaiting analysis...
        </div>
      </div>
    );
  }

  // Handle failed extraction
  if (extraction.status === 'failed') {
    return (
      <div className={cn('space-y-4', className)}>
        <div className="flex items-start justify-between">
          <h3 className="text-sm font-semibold text-primary mb-1">
            Taxonomy Classification
          </h3>
          {canReExtract && (
            <button
              onClick={handleReExtract}
              disabled={isReExtracting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={cn('h-4 w-4', isReExtracting && 'animate-spin')} />
              {isReExtracting ? 'Re-extracting...' : 'Re-extract'}
            </button>
          )}
        </div>
        <div className="p-4 rounded-lg bg-danger/10 border border-danger/30">
          <div className="flex items-center gap-2 text-danger">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Extraction Failed</span>
          </div>
          {extraction.error && (
            <p className="mt-2 text-sm text-danger/80">{extraction.error}</p>
          )}
        </div>
      </div>
    );
  }

  // Create a map of values by fieldId for easy lookup
  const valuesByField = new Map<string, TaxonomyValue>();
  extraction.values.forEach((value) => {
    valuesByField.set(value.fieldId, value);
  });

  // Sort fields by position
  const sortedFields = [...schema.fields].sort((a, b) => a.position - b.position);

  // Find deprecated field values (values with no matching field in schema)
  const schemaFieldIds = new Set(schema.fields.map((f) => f.fieldId));
  const deprecatedValues = extraction.values.filter(
    (value) => !schemaFieldIds.has(value.fieldId)
  );

  // Check for schema version mismatch
  const isStale = extraction.schemaVersion !== schema.version;

  // Get current value for editing field
  const editingValue = editingField ? valuesByField.get(editingField.fieldId) ?? null : null;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-primary mb-1">
            Taxonomy Classification
          </h3>
          <p className="text-xs text-secondary">
            AI-extracted values with confidence scores
            {canEdit && ' • Click edit to override'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {extraction.status === 'partial' && (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-pill bg-warning/10 text-warning text-xs">
              <AlertCircle className="h-3 w-3" />
              <span>Partial</span>
            </div>
          )}

          {/* Re-extract button */}
          {canReExtract && (
            <button
              onClick={handleReExtract}
              disabled={isReExtracting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Re-extract taxonomy values (preserves manual overrides)"
            >
              <RefreshCw className={cn('h-4 w-4', isReExtracting && 'animate-spin')} />
              {isReExtracting ? 'Re-extracting...' : 'Re-extract'}
            </button>
          )}
        </div>
      </div>

      {/* Schema version mismatch warning */}
      {isStale && (
        <div className="p-3 rounded-lg bg-warning/10 border border-warning/30">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-warning text-sm">
              <AlertCircle className="h-4 w-4" />
              <span>
                Schema has been updated since extraction.
              </span>
            </div>
            {canReExtract && (
              <button
                onClick={handleReExtract}
                disabled={isReExtracting}
                className="text-sm font-medium text-warning hover:underline disabled:opacity-50"
              >
                Re-extract now
              </button>
            )}
          </div>
        </div>
      )}

      {/* Values grid */}
      <div className="grid gap-3 sm:grid-cols-2">
        {sortedFields.map((field) => {
          const value = valuesByField.get(field.fieldId);

          if (!value) {
            return (
              <div
                key={field.fieldId}
                className="p-4 rounded-lg border border-border-subtle border-dashed bg-surface-subtle"
              >
                <div className="flex items-start justify-between">
                  <h4 className="text-sm font-semibold text-secondary">
                    {field.name}
                  </h4>
                  {canEdit && (
                    <button
                      onClick={() => setEditingField(field)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      aria-label={`Add value for ${field.name}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <p className="text-xs text-muted mt-1">No value extracted</p>
              </div>
            );
          }

          return (
            <ValueCard
              key={field.fieldId}
              field={field}
              value={value}
              showReasoning={showReasoning}
              canEdit={canEdit}
              onEdit={() => setEditingField(field)}
            />
          );
        })}

        {/* Deprecated field values (field deleted from schema but values exist) */}
        {deprecatedValues.map((value) => (
          <div
            key={value.fieldId}
            className="p-4 rounded-lg border border-warning/30 bg-warning/5"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-warning flex-shrink-0" />
                <h4 className="text-sm font-semibold text-warning">
                  Deprecated Field
                </h4>
              </div>
              <span className="px-2 py-0.5 rounded-pill bg-warning/10 text-warning text-xs">
                {value.optionId}
              </span>
            </div>
            <p className="text-xs text-muted mt-2">
              This field was removed from the schema. Value will be cleared on re-extraction.
            </p>
            {value.reasoning && showReasoning && (
              <p className="text-xs text-secondary mt-2 italic">
                {value.reasoning}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Extraction metadata */}
      <div className="text-xs text-muted pt-2">
        Extracted {new Date(extraction.extractedAt).toLocaleString()} |
        Schema v{extraction.schemaVersion}
      </div>

      {/* Editor modal */}
      {editingField && (
        <TaxonomyValueEditor
          field={editingField}
          currentValue={editingValue}
          allFields={schema.fields}
          allValues={valuesByField}
          isOpen={true}
          onClose={() => setEditingField(null)}
          onSave={handleSaveValue}
          isSaving={isUpdating}
        />
      )}
    </div>
  );
}
